import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  Shield,
  AlertTriangle,
  CheckCircle2,
  ThumbsUp,
  Scale,
  Award,
  Sparkles,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import { colors } from "../config/site";

/**
 * "Spot the Unfair Lease Clause" — interactive learning game.
 *
 * 10 real (anonymized) lease clause snippets. User decides Fair vs Watch Out.
 * After each: reveal the correct answer + short explanation + PA tenant right + what to do.
 * Final: score, badge, category breakdown, CTA.
 */

// isUnfair: true → correct answer is "Watch Out"
// isUnfair: false → correct answer is "Fair"
const CLAUSES = [
  {
    id: 1,
    category: "Discrimination",
    text: "Tenant agrees that this unit is not suitable for families with children under 12 years of age.",
    isUnfair: true,
    explanation:
      "This is illegal. Federal Fair Housing law protects families with children — a landlord cannot restrict a rental by family status or children's ages.",
    yourRight:
      "You are protected by the federal Fair Housing Act. Landlords may not discriminate based on familial status.",
    whatToDo:
      "File a complaint with HUD (1-800-669-9777) or the PA Human Relations Commission. This clause is unenforceable.",
  },
  {
    id: 2,
    category: "Fees",
    text: "Rent is due on the 1st of the month. A late fee of 5% will be assessed if rent is received after the 5th.",
    isUnfair: false,
    explanation:
      "This is fair. A late fee of 5% with a 5-day grace period is standard, reasonable, and enforceable in PA.",
    yourRight:
      "Late fees in PA are generally acceptable if they are reasonable (typically under 10% of monthly rent) and clearly stated in the lease.",
    whatToDo:
      "Set up autopay or a monthly reminder — a small late fee like this is easy to avoid with a system.",
  },
  {
    id: 3,
    category: "Right of Entry",
    text: "Landlord may enter the premises at any time, with or without notice, to inspect the property.",
    isUnfair: true,
    explanation:
      "This clause is problematic. While PA does not have a specific statutory notice requirement, courts generally uphold the tenant's right to reasonable notice (usually 24 hours) except in emergencies.",
    yourRight:
      "You have a right to quiet enjoyment of your home. Landlords should give reasonable notice (typically 24 hours) before non-emergency entry.",
    whatToDo:
      "Ask the landlord to amend the clause to require 24-hour notice except in emergencies. Put your request in writing.",
  },
  {
    id: 4,
    category: "Security Deposit",
    text: "Tenant shall pay a security deposit equal to three (3) months' rent, non-refundable.",
    isUnfair: true,
    explanation:
      "This is illegal in PA. Security deposits cannot exceed 2 months' rent in the first year of tenancy — and they are always refundable (minus lawful deductions).",
    yourRight:
      "Under PA law, security deposits are capped at 2 months rent in year one, then 1 month for subsequent years. They must be returned within 30 days of move-out with an itemized list of deductions.",
    whatToDo:
      "Refuse to sign until this is corrected to a lawful amount. If already signed, you can demand the excess back.",
  },
  {
    id: 5,
    category: "Repairs & Habitability",
    text: "Tenant accepts the property in 'as-is' condition and agrees the landlord has no obligation to make any repairs during the tenancy.",
    isUnfair: true,
    explanation:
      "This clause is unenforceable. PA landlords have a legal duty to provide and maintain habitable housing — no lease clause can waive that.",
    yourRight:
      "The Implied Warranty of Habitability requires landlords to maintain heat, hot water, plumbing, structural safety, and pest-free conditions.",
    whatToDo:
      "This clause cannot override the law, but push back before signing. Document all pre-existing issues at move-in with photos and a written statement.",
  },
  {
    id: 6,
    category: "Right of Entry",
    text: "Landlord may enter the premises with at least 24 hours' written notice for inspections, repairs, or to show the unit, except in cases of emergency.",
    isUnfair: false,
    explanation:
      "This is fair and follows best practices. 24-hour notice for non-emergency entry protects both parties.",
    yourRight:
      "This clause respects your right to quiet enjoyment. It is what a good lease should look like.",
    whatToDo:
      "Keep this clause. Sign confidently — this is a landlord who understands tenant rights.",
  },
  {
    id: 7,
    category: "Retaliation",
    text: "If tenant contacts any government agency regarding the condition of the property, the landlord reserves the right to terminate the lease immediately.",
    isUnfair: true,
    explanation:
      "This is illegal. PA law prohibits retaliation against tenants who report code violations or exercise their legal rights.",
    yourRight:
      "You have the right to report habitability issues to Licenses & Inspections or a housing authority without fear of retaliation. If a landlord tries to evict you within 6 months of a complaint, it's presumed retaliatory.",
    whatToDo:
      "Never sign a lease with this clause. If you already have and face retaliation, contact a tenant rights attorney — many offer no-cost consultations.",
  },
  {
    id: 8,
    category: "Auto-Renewal",
    text: "This lease will automatically renew for another 12-month term unless the tenant provides written notice 90 days before the end date.",
    isUnfair: true,
    explanation:
      "This is a red flag. 90 days is longer than typical (30-60 days is standard) and can trap tenants into another year if they miss the window.",
    yourRight:
      "You can and should negotiate this clause. Standard notice periods for lease non-renewal are 30-60 days.",
    whatToDo:
      "Ask to change it to 30 or 60 days. If they refuse, put reminders in your calendar 100 days before the lease ends. Send non-renewal notice via certified mail.",
  },
  {
    id: 9,
    category: "Fees",
    text: "Pets are allowed with a one-time refundable pet deposit of $250 and a monthly pet rent of $25.",
    isUnfair: false,
    explanation:
      "This is fair. Pet deposits and pet rent are standard in PA and reasonable in amount.",
    yourRight:
      "PA has no cap on pet deposits, but they should be reasonable. A refundable pet deposit is more tenant-friendly than a non-refundable pet fee.",
    whatToDo:
      "If you have a service animal or emotional support animal (with documentation), these fees legally cannot apply to you.",
  },
  {
    id: 10,
    category: "Waivers",
    text: "Tenant waives the right to a jury trial and any right to file suit against the landlord for any claim arising under this lease.",
    isUnfair: true,
    explanation:
      "This is unenforceable and a major red flag. You cannot legally waive your right to seek legal remedy for a landlord's breach of the lease or tenant rights law.",
    yourRight:
      "You always retain the right to take legal action against a landlord who violates the lease or the law — no clause can waive that.",
    whatToDo:
      "Cross this clause out before signing (both parties initial the change), or refuse to sign. This landlord may be signaling they anticipate legal problems.",
  },
];

const scoreToBadge = (correct, total) => {
  const pct = (correct / total) * 100;
  if (pct >= 80)
    return {
      label: "Lease Literacy Pro",
      color: colors.primary,
      icon: Award,
      headline: "You've got sharp eyes for shady clauses.",
      summary:
        "You can spot the traps and know your rights. Share this quiz with someone who's about to sign a lease — they'll thank you later.",
    };
  if (pct >= 50)
    return {
      label: "Alert Renter",
      color: colors.gold,
      icon: Shield,
      headline: "You're catching a lot — and there's room to sharpen.",
      summary:
        "You spotted many of the traps. Brush up on the specific areas below and you'll be walking into any lease conversation with confidence.",
    };
  return {
    label: "Lease Rookie",
    color: colors.terracotta,
    icon: Scale,
    headline: "This is exactly why lease literacy matters.",
    summary:
      "Don't feel bad — lease language is designed to be confusing. Now you know what to watch for. Our Lease Literacy workshop is built for this.",
  };
};

const SpotTheUnfairLease = () => {
  const [step, setStep] = useState(0); // 0=intro, 1..N=question, N+1=result
  const [answers, setAnswers] = useState({}); // { [clauseId]: chosenBoolean }
  const [revealed, setRevealed] = useState(false);

  const total = CLAUSES.length;
  const isIntro = step === 0;
  const isResult = step === total + 1;
  const current = !isIntro && !isResult ? CLAUSES[step - 1] : null;

  const submitAnswer = (guessIsUnfair) => {
    if (!current || revealed) return;
    setAnswers((prev) => ({ ...prev, [current.id]: guessIsUnfair }));
    setRevealed(true);
  };

  const next = () => {
    setRevealed(false);
    setStep((s) => s + 1);
  };

  const restart = () => {
    setAnswers({});
    setRevealed(false);
    setStep(0);
  };

  // Score
  const correctCount = CLAUSES.reduce((acc, c) => {
    if (answers[c.id] === undefined) return acc;
    return acc + (answers[c.id] === c.isUnfair ? 1 : 0);
  }, 0);
  const badge = isResult ? scoreToBadge(correctCount, total) : null;
  const BadgeIcon = badge?.icon;

  // Category performance
  const categoryStats = {};
  CLAUSES.forEach((c) => {
    if (answers[c.id] === undefined) return;
    if (!categoryStats[c.category])
      categoryStats[c.category] = { correct: 0, total: 0 };
    categoryStats[c.category].total += 1;
    if (answers[c.id] === c.isUnfair) categoryStats[c.category].correct += 1;
  });
  const weakestCategories = Object.entries(categoryStats)
    .filter(([, s]) => s.correct < s.total)
    .map(([cat, s]) => ({ cat, pct: Math.round((s.correct / s.total) * 100) }))
    .sort((a, b) => a.pct - b.pct)
    .slice(0, 3);

  const progressPct = isIntro
    ? 0
    : isResult
    ? 100
    : Math.round(((step - 1) / total) * 100);

  const currentGuess = current ? answers[current.id] : undefined;
  const isCorrect = current && revealed && currentGuess === current.isUnfair;

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      <Header />
      <main className="pt-28 pb-20 px-4">
        <div className="container mx-auto max-w-3xl">
          <Link
            to="/"
            className="inline-flex items-center text-sm font-semibold mb-6"
            style={{ color: colors.primary }}
            data-testid="lease-game-back-home"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          <div
            className="rounded-3xl shadow-lg overflow-hidden"
            style={{ backgroundColor: colors.white }}
          >
            {/* Progress bar */}
            <div className="h-1.5 w-full" style={{ backgroundColor: colors.cream }}>
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${progressPct}%`,
                  background: `linear-gradient(90deg, ${colors.primary}, ${colors.terracotta})`,
                }}
              />
            </div>

            <div className="p-8 lg:p-12">
              {/* INTRO */}
              {isIntro && (
                <div data-testid="lease-game-intro">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
                    style={{
                      backgroundColor: colors.terracotta + "22",
                      color: colors.terracottaDark,
                    }}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Lease Detective
                  </div>
                  <h1
                    className="text-3xl lg:text-5xl font-bold leading-tight mb-5"
                    style={{ color: colors.charcoal }}
                  >
                    Lease Detective
                  </h1>
                  <p className="text-lg mb-5" style={{ color: colors.textMuted }}>
                    You&apos;re the detective. Ten real (anonymized) lease clauses — some are standard,
                    some cross the line or are outright illegal in Pennsylvania. Can you catch the ones
                    that shouldn&apos;t be there?
                  </p>
                  <p className="text-base mb-7" style={{ color: colors.textMuted }}>
                    Instant feedback after each clause — and you&apos;ll learn what your rights are as a
                    Pennsylvania renter.
                  </p>
                  <div
                    className="rounded-2xl p-5 mb-7 flex gap-4 items-start"
                    style={{ backgroundColor: colors.cream }}
                  >
                    <Scale
                      className="w-6 h-6 flex-shrink-0 mt-0.5"
                      style={{ color: colors.primary }}
                    />
                    <div>
                      <p
                        className="text-xs uppercase tracking-widest font-bold mb-1"
                        style={{ color: colors.sage }}
                      >
                        Educational, not legal advice
                      </p>
                      <p className="text-sm" style={{ color: colors.charcoal }}>
                        This module teaches concepts based on general Pennsylvania tenant law.
                        For a specific situation, talk to a tenant rights attorney.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => setStep(1)}
                    className="rounded-full px-7 py-6 text-base font-semibold text-white shadow-md hover:shadow-xl transition-all"
                    style={{ backgroundColor: colors.primary }}
                    data-testid="lease-game-start-btn"
                  >
                    Start Simulation
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* QUESTION */}
              {current && (
                <div data-testid={`lease-game-question-${step}`}>
                  <p
                    className="text-xs uppercase tracking-widest font-bold mb-3"
                    style={{ color: colors.terracotta }}
                  >
                    Clause {step} of {total} · {current.category}
                  </p>
                  <h2
                    className="text-xl lg:text-2xl font-bold mb-2"
                    style={{ color: colors.charcoal }}
                  >
                    Read this clause. Is it fair — or a red flag?
                  </h2>

                  {/* The clause */}
                  <blockquote
                    className="my-6 p-5 lg:p-6 rounded-2xl italic text-base lg:text-lg leading-relaxed"
                    style={{
                      backgroundColor: colors.ivory,
                      borderLeft: `4px solid ${colors.gold}`,
                      color: colors.charcoal,
                    }}
                    data-testid="lease-clause-text"
                  >
                    &ldquo;{current.text}&rdquo;
                  </blockquote>

                  {/* Answer buttons */}
                  {!revealed && (
                    <div className="grid sm:grid-cols-2 gap-3 mb-4">
                      <button
                        type="button"
                        onClick={() => submitAnswer(false)}
                        className="rounded-2xl p-5 border-2 transition-all hover:shadow-md"
                        style={{
                          borderColor: colors.sage,
                          backgroundColor: colors.white,
                          color: colors.charcoal,
                        }}
                        data-testid="lease-answer-fair"
                      >
                        <ThumbsUp
                          className="w-6 h-6 mb-2 mx-auto"
                          style={{ color: colors.sage }}
                        />
                        <div className="font-bold">Fair</div>
                        <div className="text-xs opacity-70">Standard clause</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => submitAnswer(true)}
                        className="rounded-2xl p-5 border-2 transition-all hover:shadow-md"
                        style={{
                          borderColor: colors.terracotta,
                          backgroundColor: colors.white,
                          color: colors.charcoal,
                        }}
                        data-testid="lease-answer-unfair"
                      >
                        <AlertTriangle
                          className="w-6 h-6 mb-2 mx-auto"
                          style={{ color: colors.terracotta }}
                        />
                        <div className="font-bold">Watch Out</div>
                        <div className="text-xs opacity-70">Red flag or illegal</div>
                      </button>
                    </div>
                  )}

                  {/* Reveal */}
                  {revealed && (
                    <div
                      className="rounded-2xl p-6 mb-5"
                      style={{
                        backgroundColor: isCorrect
                          ? colors.sage + "1a"
                          : colors.terracotta + "1a",
                        borderLeft: `4px solid ${
                          isCorrect ? colors.sage : colors.terracotta
                        }`,
                      }}
                      data-testid="lease-reveal"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        {isCorrect ? (
                          <CheckCircle2
                            className="w-6 h-6"
                            style={{ color: colors.sage }}
                          />
                        ) : (
                          <AlertTriangle
                            className="w-6 h-6"
                            style={{ color: colors.terracotta }}
                          />
                        )}
                        <p
                          className="text-lg font-bold"
                          style={{
                            color: isCorrect ? colors.primary : colors.terracottaDark,
                          }}
                        >
                          {isCorrect ? "You got it!" : "Not quite."}{" "}
                          <span style={{ color: colors.charcoal, fontWeight: "normal" }}>
                            {" "}
                            The correct answer is:{" "}
                            <strong>
                              {current.isUnfair ? "Watch Out" : "Fair"}
                            </strong>
                          </span>
                        </p>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <p
                            className="text-xs uppercase tracking-widest font-bold mb-1"
                            style={{ color: colors.terracotta }}
                          >
                            Why
                          </p>
                          <p className="text-sm" style={{ color: colors.charcoal }}>
                            {current.explanation}
                          </p>
                        </div>
                        <div>
                          <p
                            className="text-xs uppercase tracking-widest font-bold mb-1"
                            style={{ color: colors.primary }}
                          >
                            Your Right
                          </p>
                          <p className="text-sm" style={{ color: colors.charcoal }}>
                            {current.yourRight}
                          </p>
                        </div>
                        <div>
                          <p
                            className="text-xs uppercase tracking-widest font-bold mb-1"
                            style={{ color: colors.sage }}
                          >
                            What to Do
                          </p>
                          <p className="text-sm" style={{ color: colors.charcoal }}>
                            {current.whatToDo}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Next button appears after reveal */}
                  {revealed && (
                    <div className="flex justify-end">
                      <Button
                        onClick={next}
                        className="rounded-full px-6 py-5 font-semibold text-white"
                        style={{ backgroundColor: colors.terracotta }}
                        data-testid="lease-game-next-btn"
                      >
                        {step === total ? "See My Score" : "Next Clause"}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* RESULT */}
              {isResult && badge && (
                <div data-testid="lease-game-result">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
                    style={{ backgroundColor: badge.color + "22", color: badge.color }}
                  >
                    <BadgeIcon className="w-3.5 h-3.5" />
                    {badge.label}
                  </div>
                  <h2
                    className="text-3xl lg:text-4xl font-bold mb-3"
                    style={{ color: colors.charcoal }}
                  >
                    {badge.headline}
                  </h2>

                  <div className="flex items-center gap-5 mb-6">
                    <div
                      className="relative flex items-center justify-center w-28 h-28 rounded-full"
                      style={{
                        background: `conic-gradient(${badge.color} ${
                          (correctCount / total) * 100
                        }%, #E6DFD2 0)`,
                      }}
                    >
                      <div
                        className="w-20 h-20 rounded-full flex items-center justify-center flex-col"
                        style={{ backgroundColor: colors.white }}
                      >
                        <span
                          className="text-2xl font-bold leading-none"
                          style={{ color: badge.color }}
                          data-testid="lease-result-score"
                        >
                          {correctCount}
                        </span>
                        <span
                          className="text-xs opacity-70"
                          style={{ color: colors.charcoal }}
                        >
                          of {total}
                        </span>
                      </div>
                    </div>
                    <p className="text-base" style={{ color: colors.textMuted }}>
                      {badge.summary}
                    </p>
                  </div>

                  {/* Weakest categories */}
                  {weakestCategories.length > 0 && (
                    <div
                      className="rounded-2xl p-5 mb-7"
                      style={{ backgroundColor: colors.cream }}
                    >
                      <p
                        className="text-xs uppercase tracking-widest font-bold mb-4"
                        style={{ color: colors.sage }}
                      >
                        Areas to sharpen
                      </p>
                      <div className="space-y-2">
                        {weakestCategories.map((c) => (
                          <div
                            key={c.cat}
                            className="flex justify-between items-center py-2 border-b"
                            style={{ borderColor: "#E6DFD2" }}
                          >
                            <span
                              className="text-sm font-semibold"
                              style={{ color: colors.charcoal }}
                            >
                              {c.cat}
                            </span>
                            <span
                              className="text-xs font-bold px-2 py-0.5 rounded-full"
                              style={{
                                backgroundColor:
                                  c.pct >= 50 ? colors.gold + "44" : colors.terracotta + "33",
                                color:
                                  c.pct >= 50 ? colors.charcoal : colors.terracottaDark,
                              }}
                            >
                              {c.pct}% correct
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CTA */}
                  <div
                    className="rounded-3xl p-6 lg:p-7 text-white"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                    }}
                  >
                    <h3 className="text-xl lg:text-2xl font-bold mb-2">
                      Want to master lease language?
                    </h3>
                    <p className="text-sm opacity-90 mb-5">
                      Our Lease Literacy workshop walks through every kind of clause
                      you&apos;ll ever see — and how to negotiate the ones that need work.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link to="/programs" data-testid="lease-cta-programs">
                        <Button
                          className="rounded-full px-6 py-5 font-semibold w-full sm:w-auto"
                          style={{ backgroundColor: colors.terracotta, color: colors.white }}
                        >
                          See Our Programs
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                      <Link to="/contact" data-testid="lease-cta-contact">
                        <Button
                          className="rounded-full px-6 py-5 font-semibold w-full sm:w-auto border-white/40 border bg-transparent hover:bg-white/10"
                        >
                          Book a Workshop
                        </Button>
                      </Link>
                    </div>
                  </div>

                  <div className="flex justify-center mt-7">
                    <Button
                      onClick={restart}
                      variant="ghost"
                      className="rounded-full"
                      data-testid="lease-game-restart-btn"
                    >
                      <RefreshCw className="mr-2 w-4 h-4" />
                      Try Again
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SpotTheUnfairLease;
